import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, CreditCard, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ERP_LABELS = {
  payrexx_card: 'Payrexx – Karte online',
  payrexx_twint: 'Payrexx – TWINT',
  sumup_card: 'SumUp – Kartenterminal',
  cash: 'Bar',
  manual_bank_transfer: 'Manuelle Banküberweisung',
  credit: 'Guthaben / Gutschein',
  other: 'Sonstiges',
};

const slugify = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `art_${Date.now()}`;

export default function PaymentMethodsSettingsPage({ embedded = false }) {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [erpOptions, setErpOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/settings/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMethods(data.methods || []);
      setErpOptions(data.erp_paid_via_options || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Zahlungsarten');
    } finally {
      setLoading(false);
    }
  };

  const updateMethod = (idx, patch) => {
    setMethods((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const addMethod = () => {
    setMethods((prev) => [
      ...prev,
      { key: `art_${prev.length + 1}`, label: '', erp_paid_via: 'cash', enabled: true },
    ]);
  };

  const removeMethod = (idx) => {
    setMethods((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    // Schlüssel aus Label ableiten, falls leer, und auf Eindeutigkeit prüfen.
    const prepared = methods.map((m) => ({
      key: m.key && m.key.trim() ? m.key.trim() : slugify(m.label),
      label: (m.label || '').trim(),
      erp_paid_via: m.erp_paid_via,
      enabled: !!m.enabled,
    }));
    if (prepared.some((m) => !m.label)) {
      toast.error('Bitte für jede Zahlungsart einen Namen angeben');
      return;
    }
    const keys = prepared.map((m) => m.key);
    if (new Set(keys).size !== keys.length) {
      toast.error('Doppelte Schlüssel – bitte Namen eindeutig wählen');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API}/settings/payment-methods`,
        { methods: prepared },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMethods(data.methods || prepared);
      toast.success('Zahlungsarten gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-gray-500">Lädt...</div>;
  }

  return (
    <div className="p-8 max-w-4xl">
      {!embedded && (
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate('/settings')}
          data-testid="payment-methods-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zu Einstellungen
        </Button>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-emerald-600" />
          Zahlungsarten (Vor Ort)
        </h1>
        <p className="text-gray-600 mt-1">
          Diese Zahlungsarten erscheinen beim manuellen „Als bezahlt markieren". Jede Zahlungsart
          wird auf einen Wert der Buchhaltungssoftware (ERP) abgebildet.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Konfigurierte Zahlungsarten</CardTitle>
          <CardDescription>
            Name = Anzeige in der App · ERP-Zuordnung = wie die Zahlung in der Buchhaltung verbucht wird.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {methods.length === 0 && (
            <p className="text-sm text-gray-500">Noch keine Zahlungsarten konfiguriert.</p>
          )}
          {methods.map((m, idx) => (
            <div
              key={idx}
              className="flex flex-col sm:flex-row sm:items-center gap-3 border rounded-lg p-3 bg-gray-50/50"
              data-testid={`payment-method-row-${idx}`}
            >
              <div className="flex-1">
                <label className="text-xs text-gray-500">Name</label>
                <Input
                  value={m.label}
                  placeholder="z.B. TWINT vor Ort"
                  onChange={(e) => updateMethod(idx, { label: e.target.value })}
                  data-testid={`payment-method-label-${idx}`}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500">ERP-Zuordnung (paid_via)</label>
                <Select
                  value={m.erp_paid_via}
                  onValueChange={(v) => updateMethod(idx, { erp_paid_via: v })}
                >
                  <SelectTrigger data-testid={`payment-method-erp-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {erpOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {ERP_LABELS[opt] || opt} <span className="text-gray-400">({opt})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-4">
                <Switch
                  checked={!!m.enabled}
                  onCheckedChange={(v) => updateMethod(idx, { enabled: v })}
                  data-testid={`payment-method-enabled-${idx}`}
                />
                <span className="text-xs text-gray-500">Aktiv</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-600 self-end sm:self-center"
                onClick={() => removeMethod(idx)}
                data-testid={`payment-method-delete-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={addMethod} data-testid="payment-method-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Zahlungsart hinzufügen
            </Button>
            <Button onClick={save} disabled={saving} data-testid="payment-methods-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
